'use strict';

const REGEX = {
    phone: /^[+]?[ 0-9]{0,20}$/,
    email: /^(?![^@]{65})(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(?=.{1,255}$)((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    password: /^.{8,20}$/,
    ssn: /^(19|20)?(\d{6}([-+]|\s)\d{4}|(?!19|20)\d{10})$/,
    nif: /^[0-9]{9}$/,
    emojis: /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/,
};

/**
 * Validation Rules Object
 */
const rules = {
    /**
     * Rule: Test minimum length of form field
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateMinLength(formElement) {
        if (formElement.state.value && formElement.state.value.length < formElement.attributes.minLength) {
            return {
                valid: false,
                errorMessage: formElement.attributes.minLengthError
            };
        }

        return {
            valid: true
        };
    },

    /**
     * Rule: Test maximum length of form field
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateMaxLength(formElement) {
        if (formElement.state.value && formElement.state.value.length > formElement.attributes.maxLength) {
            return {
                valid: false,
                errorMessage: formElement.attributes.valueError
            };
        }

        return {
            valid: true
        };
    },

    /**
     * Rule: Test if the value is only alpha numeric
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateNumber(formElement) {
        if (formElement.state.value && isNaN(parseInt(formElement.state.value))) {
            return {
                valid: false,
                errorMessage: 'Hardcoded error message for number'
            };
        }

        return {
            valid: true
        };
    },

    /**
     * Rule: Test if the value is only alpha numeric and has a minimum value
     * and is above or equal to a minimum value
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateMinValue(formElement) {
        const numberValidationResult = rules.validateNumber(formElement);
        if (!numberValidationResult.valid) {
            return numberValidationResult;
        }

        if (formElement.state.value && formElement.state.value < formElement.attributes.minValue) {
            return {
                valid: false,
                errorMessage: 'Hardcoded error message for minvalue'
            };
        }

        return {
            valid: true
        };
    },

    /**
     * Rule: Test if the value is only alpha numeric and is below or equal
     * to a maximum value
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateMaxValue(formElement) {
        const numberValidationResult = rules.validateNumber(formElement);
        if (!numberValidationResult.valid) {
            return numberValidationResult;
        }

        if (formElement.state.value && formElement.state.value > formElement.attributes.maxValue) {
            return {
                valid: false,
                errorMessage: 'Hardcoded error message for maxvalue'
            };
        }

        return {
            valid: true
        };
    },

    /**
     * Rule: Test is a mandatory field has a value
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateMandatory(formElement) {
        const isValueString = typeof formElement.state.value === 'string';
        if (!formElement.attributes.mandatory ||
            (!!formElement.state.value &&
                (!isValueString || (isValueString && formElement.state.value.trim() !== null))
            )
        ) {
            return {
                valid: true
            };
        }

        return {
            valid: false,
            errorMessage: formElement.attributes.missingError
        };
    },

    /**
     * Rule: Test a value using a regular expression
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateRegEx(formElement) {
        if (!formElement.state.value) {
            return {
                valid: true
            };
        }

        const regex = new RegExp(formElement.attributes.regEx);
        if (formElement.attributes.emojiRegex) {
            if (regex.test(formElement.state.value)) {
                return {
                    valid: false,
                    errorMessage: formElement.attributes.valueError
                };
            }

            return {
                valid: true
            };
        }
        if (!regex.test(formElement.state.value)) {
            return {
                valid: false,
                errorMessage: formElement.attributes.valueError
            };
        }

        return {
            valid: true
        };
    },
    /**
     * Rule: Soft validate a value using a regular expression
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    softValidateRegEx(formElement){
        if (!formElement.state.value || !formElement.attributes.softValidateRegEx){
            return {
                valid:true
            };
        }
        const regex = new RegExp(formElement.attributes.softValidateRegEx);
        if (regex.test(formElement.state.value)) {
            return {
                valid: false,
                errorMessage: formElement.attributes.valueWarning
            };
        }
        return {
            valid:true
        };
    },

    /**
     * Rule: Prevent script tag using a regular expression
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateScriptValueRegEx(formElement){
        if (!formElement.state.value || !formElement.attributes.scriptRegEx) {
            return {
                valid: true
            };
        }

        const regex = new RegExp(formElement.attributes.scriptRegEx);
        if (regex.test(formElement.state.value)) {
            return {
                valid: false,
                errorMessage: formElement.attributes.valueError
            };
        }

        return {
            valid: true
        };
    },

    /**
     * Rule: Test for a valid email address
     * @param {JSON Object} formElement The JSON object describing the form element
     * @returns Boolean
     */
    validateEmail(formElement) {
        if (!formElement.state.value) {
            return {
                valid: true
            };
        }

        if (!REGEX.email.test(formElement.state.value.trim())) {
            return {
                valid: false,
                errorMessage: 'Hardcoded error message for email'
            };
        }

        return {
            valid: true
        };
    }
};

module.exports = rules;
